import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get, last, maxBy } from "lodash";
import { IconButton, makeStyles } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import FrontViewIsoWarehouse from "./FrontViewIsoWarehouse";
import {
  addNewField,
  updateGlobalParameterBertWarehouse,
  updateOverallWidth,
} from "../reducers/BertWarehouse/Actions";
import arrowUp from "../assets/arrowUp.svg";
import arrowDown from "../assets/arrowDown.svg";

const useStyles = makeStyles((theme) => ({
  root: { overflow: "auto", padding: "0 20px", position: "relative", width: "100%" },
  col: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    marginTop: 60,
  },
  infoContainer: { position: "relative", height: 30 },
  paletteInfo: { position: "fixed", right: 20, fontWeight: "bold" },
  supportBar: { position: "relative" },
  barImage: {
    width: "100%",
    position: "absolute",
    bottom: 0,
    zIndex: -1,
    opacity: 0.3,
    left: -5,
    display: "none",
  },
  wrapper: {
    alignItems: "center",
    display: "flex",
    marginTop: 60,
    justifyContent: "center",
  },
  flexRow: {
    display: "flex",
    color: "#3f3f3f",
  },
  iconButtonContainer: {
    width: "100%",
    bottom: "50%",
    position: "absolute",
    display: "flex",
    alignItems: "center",
    minWidth: 200,
    justifyContent: "center",
  },
  arrowHeightContainer: {
    alignSelf: "flex-start",
    marginRight: 40,
    marginTop: 60,
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    minWidth: 60,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    display: "flex",
  },
  overallWidth: {
    marginBottom: -110,
    alignSelf: "center",
    width: "100%",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    display: "flex",
  },
  arrow: {
    display: "flex",
    alignItems: "flex-start",
    width: "50%",
  },
  iconButton: {
    backgroundColor: "#59A247",
    boxShadow: "0px 2px 4px #00000080",
    borderWidth: 1,
    borderColor: "#59A247",
    borderRadius: 2,
    padding: "4px",
    "&:hover": {
      backgroundColor: "#59A247",
    },
  },
}));

const VisualizationIsoWarehouse = (props) => {
  const dispatch = useDispatch();
  const previewContainerRef = useRef();
  const classes = useStyles({ scaleFactor });
  const [fieldsUpdated, setFieldsUpdated] = useState(0);
  const [shelfBottomHeight, setShelfBottomHeight] = useState(22);
  const defaultShelfBottomHeight = 22;
  const shelfBottomHeightDifference = shelfBottomHeight - defaultShelfBottomHeight;
  const [prevDepth, setPrevDepth] = useState("frontend_depth300");
  const [prevScaleFactor, setPrevScaleFactor] = useState(8);
  const { configuration } = useSelector((state) => state.Configuration);

  const {
    fields,
    scaleFactor,
    height,
    color,
    usableFromBothSides,
    doubleShelf,
    depth,
    shelfLoad,
    overallWidth,
  } = useSelector((state) => state.BertWarehouse);

  const saveShelfHeightPerDepth = (height) => {
    if (depth !== prevDepth || scaleFactor !== prevScaleFactor) {
      setShelfBottomHeight(height);
      setPrevDepth(depth);
      setPrevScaleFactor(scaleFactor);
    }
  };
  const fieldsLength = fields.length;
  const data = last(fields);
  const width = Number(get(data, "width", 1005).match(/\d+/));
  const heightValue = Number(height.match(/\d+/));
  const scaledHeight = heightValue / scaleFactor;
  const scaledWidth = width / scaleFactor;
  let containerRef = null;
  const [containerHeight, setContainerHeight] = useState(0);
  const [prevHeight, setPrevHeight] = useState(0);
  const handleResize = (containerRef) => {
    const extraSpace = 210;
    const maxScaleFactor = 12;
    let scales = Array.from({ length: maxScaleFactor }, (_, i) => i + 1);
    scales = scales.map((scale) => {
      const scaledHighestFrameHeight = heightValue / scale;
      return { scale: scale, height: scaledHighestFrameHeight + extraSpace };
    });
    const match = maxBy(scales, (scale) => (scale.height <= containerRef.clientHeight - 10 ? scale : null));
    dispatch(updateGlobalParameterBertWarehouse("scaleFactor", match ? match.scale : maxScaleFactor));
    if (containerRef.clientHeight < containerRef.scrollHeight) {
      containerRef.scrollTop = containerRef.scrollHeight - containerRef.clientHeight;
    }
  };
  useEffect(() => {
    window.addEventListener("resize", (e) => {
      const ele = window.document.getElementById("containerRef");
      handleResize(ele);
    });
  }, []);
  useEffect(
    () => {
      if (
        configuration &&
        containerRef &&
        (containerHeight !== containerRef.scrollHeight || heightValue !== prevHeight)
      ) {
        handleResize(containerRef);
        setContainerHeight(containerRef.scrollHeight);
        setPrevHeight(heightValue);
      }
    },
    containerRef,
    height
  );
  useEffect(() => {
    previewContainerRef.current &&
      previewContainerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }, [fieldsUpdated]);

  const addField = async () => {
    setFieldsUpdated(fieldsUpdated + 1);
    await dispatch(addNewField());
    return dispatch(updateOverallWidth());
  };

  const FrontView = (props) => {
    const classes = useStyles(props);
    const overallWidthOffset = (shelfBottomHeight + shelfBottomHeightDifference) * fieldsLength;
    return (
      <div style={{ flexDirection: "column", display: "flex", justifyContent: "space-between" }}>
        <div className={classes.col}>
          {fields.map((field, i) => {
            return (
              <FrontViewIsoWarehouse
                fields={fields}
                key={field.id}
                data={field}
                height={height}
                depth={depth}
                color={color}
                shelfLoad={shelfLoad}
                fieldsLength={fieldsLength}
                usableFromBothSides={usableFromBothSides}
                doubleShelf={doubleShelf}
                scaleFactor={scaleFactor}
                shelfBottomHeight={shelfBottomHeight}
                saveShelfHeightPerDepth={saveShelfHeightPerDepth}
              />
            );
          })}
        </div>
        <div
          className={classes.overallWidth}
          style={{ width: `calc(100% - ${overallWidthOffset + "px"})`, marginLeft: -overallWidthOffset }}
        >
          <div className={classes.arrow}>
            <img src={arrowUp} style={{ transform: "rotate(-90deg)" }} />
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginLeft: -3 }} />
          </div>
          <div
            style={{ marginLeft: 20, marginRight: 20, minWidth: 80, width: 80, textAlign: "center" }}
          >{`${overallWidth} mm`}</div>
          <div className={classes.arrow}>
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginRight: -3 }} />
            <img src={arrowDown} style={{ transform: "rotate(-90deg)" }} />
          </div>
        </div>
      </div>
    );
  };
  if (scaleFactor === 0) {
    return (
      <div
        className={classes.root}
        id="containerRef"
        ref={(ele) => {
          containerRef = ele;
        }}
      ></div>
    );
  }
  return (
    <>
      <div
        className={classes.root}
        id="containerRef"
        ref={(ele) => {
          containerRef = ele;
        }}
      >
        <div className={classes.flexRow} id="screenshot-area">
          <div className={classes.arrowHeightContainer} style={{ height: scaledHeight }}>
            <img src={arrowUp} />
            <div style={{ height: "50%", width: 1, backgroundColor: "#979797", marginTop: -3 }} />
            <div style={{ marginTop: 20, marginBottom: 20 }}>{`${heightValue} mm`}</div>
            <div style={{ height: "50%", width: 1, backgroundColor: "#979797", marginBottom: -3 }} />
            <img src={arrowDown} />
          </div>
          <FrontView data={configuration} props={props} />
          <div
            className={classes.wrapper}
            id={"add-button-container"}
            style={{
              minHeight: scaledHeight,
              maxHeight: scaledHeight,
              minWidth: scaledWidth,
              width: scaledWidth,
            }}
          >
            <div ref={previewContainerRef}>
              <IconButton
                className={classes.iconButton}
                onClick={addField}
                style={{
                  marginLeft:
                    -shelfBottomHeight * (fieldsLength - 1) -
                    shelfBottomHeightDifference * (fieldsLength - 1),
                }}
              >
                <AddIcon style={{ color: "#262626" }} />
              </IconButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default VisualizationIsoWarehouse;
